import { useCallback } from 'react';
import { useGetFilterCommand } from '@atlassian/jira-command-palette-common/src/common/ui/command/filter/index.tsx';
import type { Command } from '@atlassian/jira-command-palette/src/common/types/commands/index.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { FILTER_LIMIT } from '../constants.tsx';
import { messages } from '../messages.tsx';
import type { GetQuickSearchChildResultProps } from '../types.tsx';
import { getQuickSearchFilterScope, transformQuickSearchFilterResultToFilter } from '../utils.tsx';
import { useGetViewAllCommand } from '../view-all/index.tsx';
import { ViewAllType, getViewAllUrl } from '../view-all/utils.tsx';
import type {
	UseGetQuickSearchFilterCommandsProps,
	UseGetQuickSearchFilterCommandsResponse,
} from './types.tsx';

export const useGetQuickSearchFilterCommands = ({
	section,
	showViewAll,
}: UseGetQuickSearchFilterCommandsProps): UseGetQuickSearchFilterCommandsResponse => {
	const { formatMessage } = useIntl();

	const { getFilterCommand } = useGetFilterCommand();
	const { getViewAllCommand } = useGetViewAllCommand();

	const getQuickSearchFilterCommands = useCallback(
		({ search, quickSearchResponseData, error }: GetQuickSearchChildResultProps) => {
			const filterScope = getQuickSearchFilterScope(quickSearchResponseData);

			// No results
			if (!filterScope || error || filterScope?.error || filterScope?.results?.length === 0) {
				return [];
			}

			const commands: Command[] = filterScope.results.slice(0, FILTER_LIMIT).map((filter) =>
				getFilterCommand({
					filter: transformQuickSearchFilterResultToFilter(filter),
					keywords: search,
					section,
					analytics: {
						isSearchResult: true,
					},
				}),
			);

			if (!showViewAll || filterScope.results.length <= FILTER_LIMIT) {
				return commands;
			}

			const viewAllCommand = getViewAllCommand({
				id: 'view-all-filters',
				keywords: search,
				section,
				name: formatMessage(messages.viewAllResults),

				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				url: getViewAllUrl(ViewAllType.FILTER, search)!,
				analyticsPayload: { action: 'viewAllFilters', size: filterScope.size },
			});

			return commands.concat([viewAllCommand]);
		},
		[formatMessage, getFilterCommand, getViewAllCommand, section, showViewAll],
	);

	return { getQuickSearchFilterCommands };
};
